﻿import { Configurator } from "@models/configurators/configurator";
import { ConfiguratorDI } from "@models/configurators/configurator-di";
import { CToken } from "@models/configurators/ctoken";
import { KbObject } from "@models/configurators/kb-object";
import { IField } from "@models/entity-models";
import { eFieldType } from "@models/enums";
import { Utils } from "@tools";

export class KbObjectManager {
    constructor() {
    }
    public kbObjects: any = {};
    public kbObjectIds: any = {};

    public addKbObject(o: KbObject, parent: KbObject) {
        o.name = this.getUniqueName(o, o.name);
        o.$parent = parent;
        this.kbObjects[o.name.toLowerCase()] = o;

        // if this is a nested configurator, then we don't add it to the id map.
        // Reasoning being that there will be id collisions.  
        // Like adding multiple instances of the same nested configurator
        // or even cloning a configurator
        if (!(ConfiguratorDI.instanceOf(o, CToken.Configurator) && parent)) {
            this.kbObjectIds[o.id] = o;
        }
    }

    public removeKbObject(o: KbObject) {
        if (o) {
            // remove all child kbObjects as well
            if (!ConfiguratorDI.instanceOf<Configurator>(o, CToken.Configurator)) {
                for (let prop in o) {
                    if (prop.substr(0, 1) == "$" || prop.substr(0, 1) == "_") continue;
                    if (Array.isArray(o[prop])) {
                        o[prop].forEach(oChild => {
                            if (ConfiguratorDI.instanceOf<KbObject>(oChild, CToken.KbObject)) {
                                this.removeKbObject(oChild);
                            }
                        });
                    }
                }
            }

            delete this.kbObjects[o.name.toLowerCase()];
            // if this is a nested configurator, then we don't add it to the id map.
            // Reasoning being that there will be id collisions.  
            // Like adding multiple instances of the same nested configurator
            // or even cloning a configurator
            //&& parent ** removed this since the function doesn't take a parent but on client this would be the window
            if (!(ConfiguratorDI.instanceOf(o, CToken.Configurator))) { 
                delete this.kbObjectIds[(o as Configurator).id];
            }

        }
    }

    public getByName<T extends KbObject>(name: string, throwOnFailure = true): T {
        let n = name? name.toLowerCase(): name;
        let o = this.kbObjects[n] as T;
        if (!o && throwOnFailure) {
            throw new Error("object with name " + name + " does not exist");
        }
        return o;
    }

    public get<T extends KbObject>(id: string, throwOnFailure = true): T {
        let o = this.kbObjectIds[id] as T;
        if (!o && throwOnFailure) {
            throw new Error("object with id " + id + " does not exist");
        }
        return o;
    }

    public getByIdOrName<T extends KbObject>(idOrName: string, throwOnFailure = true): T{
        let o = this.get<T>(idOrName, false);
        if (!o) o = this.getByName<T>(idOrName, false);
        if (!o && throwOnFailure) {
            throw new Error("object with id or name " + idOrName + " does not exist");
        }
        return o;
    }

    public hasNamedObject(name: string): boolean {
        return this.kbObjects.hasOwnProperty(name.toLowerCase());
    }

    public removeKbObjectById(id: string) {
        delete this.kbObjectIds[id];
    }

    public addKbObjectWithId(id: string, o: KbObject) {
        this.kbObjectIds[id] = o;
    }

    public removeKbObjectNameReference(name: string) {
        delete this.kbObjects[name.toLowerCase()];
    }

    public addKbObjectNameReference(name: string, o: KbObject) {
        this.kbObjects[name.toLowerCase()] = o;
    }

    /**
     * finds a unique name for given object in the context of the collection of kbObjects
     */
    public getUniqueName(o: KbObject, proposed?: string) {
        let counter = 0;
        proposed = proposed || o.$type || "Object"; // IE does not jive with constructor.name so we need a fallback
        let name = proposed;
        let nameIsUnique = (proposed: string, o?: KbObject) => {
            return !this.hasNamedObject(proposed.toLowerCase()) || this.getByName(proposed) === o;
        };
        while (!nameIsUnique(name, o)) {
            counter++;
            name = proposed + counter.toString();
        }
        return name;
    }

    /**
     * clear out all of the id's and names from this instance
     */
    public clear() {
        this.kbObjectIds = {};
        this.kbObjects = {};
    }

    public create<T extends KbObject>(token: CToken, kom: KbObjectManager): T {
        let k = ConfiguratorDI.get(token);
        return new k.class(kom) as T;
    }

    public recurse(source: any, target: KbObject) {
        let kom = target.$manager;
        for (let prop in source) {
            if (source.hasOwnProperty(prop)) {
                if (Array.isArray(source[prop])) {
                    if (!target[prop]) {
                        target[prop] = [];
                    }

                    source[prop].forEach(sourceItem => {
                        let targetItem;
                        if (sourceItem == null) {
                            target[prop].push(sourceItem);
                            return;
                        }

                        // Hack: Because method of manufacture is a dynamic type on the server-side, it is
                        // problemmatic for Newtonsoft to properly assign a $type parameter.
                        if (prop == "materials") sourceItem.$type = CToken.PartMaterial.key;
                        if (prop == "operations") sourceItem.$type = CToken.PartOperation.key;
                        if (sourceItem.$type) {
                            let komArg = kom;
                            if (sourceItem.$type == CToken.Configurator.key) {
                                komArg = new KbObjectManager();
                            }
                            targetItem = this.create(CToken.withKey(sourceItem.$type), komArg);
                            target[prop].push(targetItem);

                            // recurse before adding it to the $manager so that the name is already resolved
                            this.recurse(sourceItem, targetItem);

                            if (ConfiguratorDI.instanceOf<KbObject>(targetItem, CToken.KbObject)) {
                                kom.addKbObject(targetItem, target);
                            }
                        } else if (typeof sourceItem === "object") {
                            targetItem = {};
                            target[prop].push(targetItem);
                            this.recurse(sourceItem, targetItem);
                        } else { // must be a value type
                            let propDef = Object.getOwnPropertyDescriptor(target.constructor.prototype, prop);
                            if (!propDef || propDef.set) { //if prop has a setter
                                // Only do anything to the target if the property has a setter.
                                target[prop].push(sourceItem);
                            }
                        }
                    });
                } else {
                    let propDef = Object.getOwnPropertyDescriptor(target.constructor.prototype, prop);
                    if (prop == "rootPartRev" && source[prop]) source[prop].$type = CToken.PartRev.key;
                    if (prop == "methodOfManufacture" && source[prop]) source[prop].$type = CToken.MethodOfManufacture.key;
                    if (!propDef || propDef.set) { //if prop has a setter
                        // This fixes the following bug:
                        //
                        // If you created two or more nested configurators that had an upload field, then uploading a file to one of them would also upload it for the other.
                        // the reason for this was each upload field was referencing the same default value, which is an empty upload field. This line forces each instance
                        // of an upload field to reference its own default value to break that coupling.
                        if (source.$type == CToken.Field.key && (<IField>source).type == eFieldType.upload && prop == "value") {
                            target[prop] = Utils.extend({}, source[prop]);
                        }
                        else if (source[prop] && source[prop].$type) {
                            let targetItem = this.create(CToken.withKey(source[prop].$type), kom);
                            target[prop] = targetItem;
                            this.recurse(source[prop], targetItem);
                        } else {
                            target[prop] = source[prop];
                        }
                    }    
                }
            }
        }
    }
}
